import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Heart, Sparkles } from "lucide-react";

export default function AboutPage() {
  return (
    <main className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="py-6 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <Link 
            to="/" 
            data-testid="back-to-home"
            className="inline-flex items-center gap-2 text-[#52525B] hover:text-[#F58227] transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <section className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Title */}
            <div className="mb-12">
              <p className="text-sm font-medium uppercase tracking-widest text-[#0CBCBF] mb-4">
                About
              </p>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-[#27272A] mb-6">
                Why we built this
              </h1>
            </div>

            {/* Story */}
            <div className="prose prose-lg max-w-none space-y-6 text-[#52525B]">
              <p className="text-xl leading-relaxed">
                "Where was i" started with a familiar frustration: the moment you pick up your phone 
                for something important, and three minutes later you're watching cat videos with no 
                memory of why you unlocked it.
              </p>

              <p className="leading-relaxed">
                For those of us with ADHD — or anyone navigating the fog of modern distraction — 
                these moments aren't character flaws. They're just how our minds work. The problem 
                isn't that we drift. It's that there's no gentle way back.
              </p>

              <div className="bg-[#E0F7FA]/50 rounded-2xl p-8 my-10">
                <p className="font-handwritten text-2xl text-[#0CBCBF] text-center mb-0">
                  "The goal isn't to never lose focus. It's to make returning feel safe."
                </p>
              </div>

              <p className="leading-relaxed">
                Most productivity tools assume you have the executive function to use them. 
                They demand consistency, punish forgetfulness, and pile on guilt when you inevitably 
                fall off the wagon.
              </p>

              <p className="leading-relaxed">
                We wanted something different. A tool that meets you where you are. That doesn't 
                judge the 47 open tabs or the half-finished task from three days ago. That simply 
                asks: "Where were you trying to go?"
              </p>

              <h2 className="text-2xl font-semibold text-[#27272A] mt-12 mb-4">
                What we believe
              </h2>

              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Sparkles size={20} className="text-[#F58227] mt-1 shrink-0" />
                  <span><strong>Attention is nonlinear.</strong> Your mind doesn't move in straight lines, and that's not a bug.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Sparkles size={20} className="text-[#F58227] mt-1 shrink-0" />
                  <span><strong>Shame doesn't help.</strong> Guilt spirals make everything worse. Kindness gets you moving.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Sparkles size={20} className="text-[#F58227] mt-1 shrink-0" />
                  <span><strong>Returning is the skill.</strong> Everyone drifts. The magic is in coming back.</span>
                </li>
              </ul>

              <div className="bg-[#FCEBDC]/50 rounded-2xl p-8 mt-12">
                <div className="flex items-center gap-3 mb-4">
                  <Heart size={24} className="text-[#F58227] fill-[#F58227]" />
                  <h3 className="text-xl font-semibold text-[#27272A] mb-0">Built with care</h3>
                </div>
                <p className="text-[#52525B] mb-0">
                  "Where was i" is built by people who know what it's like to lose the thread. 
                  We're not here to optimize you. We're here to help you find your way back — 
                  whenever you're ready.
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-16 text-center">
              <Link
                to="/"
                data-testid="about-cta-home"
                className="inline-flex items-center gap-2 bg-[#F58227] text-white hover:bg-[#D96D19] rounded-full px-8 py-4 text-lg font-bold shadow-lg hover:shadow-[#F58227]/30 transition-all active:scale-95"
              >
                Back to Home
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
